import {
  CoachSessionRemarkV2LabelMap,
  classTimeTypeSchema,
  coachSessionRemarkV2Schema,
  lessonKindSchema,
  studentYearSchema,
} from '@airreading/common/data';
import { endOfDay, isAirAdmin } from '@airreading/common';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { lessonTypeaheadSearch, useOrgOptions, useSchoolOptions } from '@/lib/api-call-hooks';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { classTimeTypesFilterOption, lessonKindsFilterOption } from '@/lib/form-utils';
import { paginationRequestSchema } from '@/lib/pagination';
import { pluralize } from '@/lib/string-utils';
import { getStudentYearOptions } from '@/lib/student-utils';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions';
import { CreateSessionModal } from '@/routes/_layout/sessions/-components/create-session-modal';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table';
import { sessionsTabs } from '@/routes/_layout/sessions/-components/tabs';
import { FrequentTimeZones } from '@airreading/common';
import { AdminSession } from '@airreading/common/data';
import { validateRouteRole } from '@/lib/routing';

export const Route = createFileRoute('/_layout/sessions/past/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: PastSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    orgs: z.string().array().optional(),
    schools: z.string().array().optional(),
    grades: z.array(studentYearSchema).optional(),
    lessons: z.string().array().optional(),
    lessonKinds: z.array(lessonKindSchema).optional(),
    sessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
  }),
});

function PastSessionsIndex() {
  const queryClient = useQueryClient();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      lessonKinds: [[selectedLessonKinds, activeSelectedLessonKinds], setSelectedLessonKinds],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      sessionRemarksV2: [[selectedSessionRemarksV2, activeSelectedSessionRemarksV2], setSelectedSessionRemarksV2],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(
    Route.id,
    [
      'query',
      'orgs',
      'schools',
      'grades',
      'lessonKinds',
      'lessons',
      'sessionRemarksV2',
      'startDate',
      'endDate',
      'classTypes',
    ],
    ['grades', 'lessons', 'lessonKinds', 'sessionRemarksV2'],
  );

  const endOfTodayInCST = endOfDay(Temporal.Now.instant(), FrequentTimeZones.UsCentral);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: selectedStartDate
          ? Temporal.PlainDate.from(selectedStartDate)
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .startOfDay()
              .toInstant()
              .toString()
          : undefined,
        startOnOrBefore: selectedEndDate
          ? Temporal.PlainDate.from(selectedEndDate)
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : endOfTodayInCST.toString(),
        query: activeQuery,
        orgs: activeSelectedOrgs,
        schools: activeSelectedSchools,
        grades: activeSelectedGrades,
        lessonKinds: activeSelectedLessonKinds,
        lessons: activeSelectedLessons,
        sessionRemarksV2: activeSelectedSessionRemarksV2,
        classTypes: activeSelectedClassTypes,
      },
      sortBy: ['start-desc'],
    }),
  );

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;
  const allSchoolOptions = useSchoolOptions();
  const allOrgOptions = useOrgOptions();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[250px] lg:w-[350px] xl:w-[400px] 2xl:w-[450px]"
          placeholder="Search sessions by student, teacher, email or lesson name"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="District"
          options={allOrgOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Class type"
          options={classTimeTypesFilterOption}
          selectedValues={selectedClassTypes ?? []}
          onChange={setSelectedClassTypes}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date"
        />
        <TableMoreFiltersMenu
          activeFiltersCount={activeFiltersCount}
          filterItems={[
            {
              title: 'Grade',
              filterComponent: (
                <TableFilterDropdown
                  title="Grade"
                  options={getStudentYearOptions()}
                  selectedValues={selectedGrades ?? []}
                  onChange={setSelectedGrades}
                />
              ),
            },
            {
              title: 'Lesson type',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson type"
                  options={lessonKindsFilterOption}
                  selectedValues={selectedLessonKinds ?? []}
                  onChange={setSelectedLessonKinds}
                />
              ),
            },
            {
              title: 'Lesson name',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson name"
                  options={lessonTypeaheadSearch(queryClient)}
                  selectedValues={selectedLessons ?? []}
                  onChange={setSelectedLessons}
                />
              ),
            },
            {
              title: 'Remarks',
              filterComponent: (
                <TableFilterDropdown
                  title="Remarks"
                  options={coachSessionRemarkV2Schema.options.map((type) => ({
                    label: CoachSessionRemarkV2LabelMap[type],
                    value: type,
                  }))}
                  selectedValues={selectedSessionRemarksV2 ?? []}
                  onChange={setSelectedSessionRemarksV2}
                />
              ),
            },
          ]}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      allOrgOptions,
      selectedOrgs,
      setSelectedOrgs,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedGrades,
      setSelectedGrades,
      selectedLessonKinds,
      setSelectedLessonKinds,
      queryClient,
      selectedLessons,
      setSelectedLessons,
      selectedClassTypes,
      setSelectedClassTypes,
      selectedSessionRemarksV2,
      setSelectedSessionRemarksV2,
      selectedStartDate,
      selectedEndDate,
      resetFilters,
      setQuery,
      setSelectedStartDate,
      setSelectedEndDate,
      activeFiltersCount,
    ],
  );

  const topBarRightComponents = <CreateSessionModal trigger={<Button>New session</Button>} />;

  return (
    <InfoPage headerText="Sessions" tabs={sessionsTabs('Past sessions')}>
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onPaginationChange,
          onRowSelectionChange,
          getRowId,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        topBarRightComponents={topBarRightComponents}
        bulkActionsMenuItems={bulkActionsMenuItems}
        from="past"
      />
    </InfoPage>
  );
}
